import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';

interface GoatIncomeLog {
  id: string;
  location_id: string;
  shed_id?: string;
  sale_date: string;
  amount_earned: number;
  quantity_sold_kg?: number;
  goat_count?: number;
  avg_weight_kg?: number;
  buyer_name?: string;
  buyer_contact?: string;
  remarks?: string;
  created_at: string;
  created_by: string;
  shed_name?: string;
}

const GoatIncomeLogsPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [logs, setLogs] = useState<GoatIncomeLog[]>([]);
  const [sheds, setSheds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [selectedShed, setSelectedShed] = useState('');

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadSheds();
    }
  }, [selectedLocation?.id, businessType]);

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadLogs();
    }
  }, [selectedLocation?.id, businessType, startDate, endDate, selectedShed, sheds]);

  const loadSheds = async () => {
    try {
      const response = await apiClient.get(`/goat/sheds?location_id=${selectedLocation?.id}`);
      setSheds(response.data || []);
    } catch (err: any) {
      console.error('Failed to load sheds:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ location_id: selectedLocation?.id || '' });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedShed) params.append('shed_id', selectedShed);
      
      const response = await apiClient.get(`/goat/income-logs?${params.toString()}`);
      const logsData = response.data || [];
      
      // Enrich with shed names
      const enrichedLogs = logsData.map((log: GoatIncomeLog) => ({
        ...log,
        shed_name: log.shed_id ? (sheds.find(s => s.id === log.shed_id)?.name || 'Unknown Shed') : 'N/A'
      }));
      
      setLogs(enrichedLogs);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load income logs'));
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    const defaultDates = getDefaultDates();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setSelectedShed('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTotalAmount = () => {
    return logs.reduce((sum, log) => sum + log.amount_earned, 0).toFixed(2);
  };

  const getTotalQuantity = () => {
    return logs.reduce((sum, log) => sum + (log.quantity_sold_kg || 0), 0).toFixed(1);
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/goat/logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">💰 Income Logs</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <Link
          to="/goat/data-entry/income"
          className="btn-touch bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Income</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="form-label">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Shed</label>
            <select
              value={selectedShed}
              onChange={(e) => setSelectedShed(e.target.value)}
              className="form-input"
            >
              <option value="">All Sheds</option>
              {sheds.map((shed) => (
                <option key={shed.id} value={shed.id}>
                  {shed.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={clearFilters}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            Reset Filters
          </button>
          <button
            onClick={loadLogs}
            className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {logs.length > 0 && (
        <div className="card p-4">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-xs text-green-600 font-medium">Total Income</div>
              <div className="text-lg font-bold text-green-700">₹{getTotalAmount()}</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-xs text-blue-600 font-medium">Total Quantity (kg)</div>
              <div className="text-lg font-bold text-blue-700">{getTotalQuantity()}</div>
            </div>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Income Records ({logs.length})
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : logs.length > 0 ? (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-semibold text-gray-900">{log.shed_name}</span>
                      <span className="text-sm text-gray-500">{formatDate(log.sale_date)}</span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="text-lg font-bold text-green-600">₹{log.amount_earned.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      {log.quantity_sold_kg && <div>Quantity: {log.quantity_sold_kg} kg</div>}
                      {log.goat_count && <div>Goats: {log.goat_count}</div>}
                      {log.avg_weight_kg && <div>Avg Weight: {log.avg_weight_kg} kg</div>}
                      {log.buyer_name && <div>Buyer: {log.buyer_name}</div>}
                      {log.remarks && <div className="text-xs text-gray-500">Remarks: {log.remarks}</div>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">💰</div>
            <p className="text-gray-500 mb-4">No income records found</p>
            <Link
              to="/goat/data-entry/income"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add First Income Entry
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoatIncomeLogsPage;
